import { useMemo } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import Tooltip from "@Components/Tooltip/Tooltip";
import StarRating from "@Components/StarRating";
import RatingOverview from "@Pages/PropertyPage/components/ReviewsSectionV2/components/RatingOverview";
import { buildReviewsData } from "@Pages/PropertyPage/components/ReviewsSectionV2/buildReviewsData";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./ReviewSummaryRating.module.scss";

export interface ReviewSummary {
  rating?: Record<string, string | number>;
  summary?: string;
}

interface ReviewSummaryRatingProps {
  reviews_rating: any;
  reviews_count?: number;
  review_summary?: ReviewSummary;
  className?: string;
  testId?: string;
  propertyId?: string | number;
}

const hasReviewSummaryContent = (reviewSummary?: ReviewSummary) =>
  Boolean(
    reviewSummary?.summary?.trim() ||
      (reviewSummary?.rating &&
        Object.values(reviewSummary.rating).some((value) => value != null && value !== "")),
  );

const ReviewSummaryRating = ({
  reviews_rating,
  reviews_count,
  review_summary,
  className = "",
  testId = "searchPage-inventoryCard-trustpilotPill",
  propertyId,
}: ReviewSummaryRatingProps) => {
  const isReviewsV2Enabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.REVIEWS_SECTION_V2);

  const overviewData = useMemo(() => {
    if (!isReviewsV2Enabled) return null;
    if (!hasReviewSummaryContent(review_summary)) return null;

    const built = buildReviewsData({
      reviewsRating: reviews_rating,
      reviewSummary: review_summary as { summary?: string; rating?: Record<string, string> },
    });

    return {
      ...built,
      reviewCount: reviews_count ?? built.reviewCount,
    };
  }, [isReviewsV2Enabled, review_summary, reviews_rating, reviews_count]);

  const starRating = (
    <StarRating
      rating={reviews_rating}
      reviewCount={reviews_count}
      className={className}
      testId={testId}
    />
  );

  if (!overviewData) {
    return starRating;
  }

  return (
    <Tooltip
      direction="bottom"
      delay={200}
      addComponent
      usePortal
      className={styles.tooltip}
      classNameWrapper={styles.tooltipWrapper}
      content={
        <RatingOverview
          overallRating={overviewData.overallRating}
          ratingLabel={overviewData.ratingLabel}
          reviewCount={overviewData.reviewCount}
          categories={overviewData.categories}
          summary={overviewData.summary}
          photos={overviewData.photos}
          propertyId={propertyId}
          isHoverPreview
        />
      }
    >
      <div
        onClick={(event) => event.preventDefault()}
        role="presentation"
        className={overviewData.summary ? styles.hasHoverSummary : undefined}
      >
        {starRating}
      </div>
    </Tooltip>
  );
};

export default wrapperHOC(ReviewSummaryRating, {
  componentName: "ReviewSummaryRating-SearchPage",
  showForChina: true,
});
